import React from 'react';
import DynamicStateService, { DynamicStateScopes } from '~features/DynamicStateService';
import {
  type FreeTrialState,
  type FreeTrialStateUser,
  type FreeTrialStateCompany,
  INITIAL_STATE_FREE_TRIAL_USER,
  INITIAL_STATE_FREE_TRIAL_COMPANY,
} from './FreeTrial';

type DynamicStateUserWithFreeTrial = {
  FreeTrial?: FreeTrialStateUser;
};

type DynamicStateCompanyWithFreeTrial = {
  FreeTrial?: FreeTrialStateCompany;
};

type FreeTrialUpdateUser = {
  scope: typeof DynamicStateScopes.User;
  state: Partial<FreeTrialState['user']>;
};

type FreeTrialUpdateCompany = {
  scope: typeof DynamicStateScopes.Company;
  state: Partial<FreeTrialState['company']>;
};

function useFreeTrialState(): [FreeTrialState, (update: FreeTrialUpdateUser | FreeTrialUpdateCompany) => void] {
  const [currentDynamicState, updateDynamicState] = DynamicStateService.useDynamicStateService<
    DynamicStateUserWithFreeTrial,
    DynamicStateCompanyWithFreeTrial
  >();

  // Safely access FreeTrial state with proper type narrowing
  const companyFreeTrial = currentDynamicState?.company?.FreeTrial;
  const userFreeTrial = currentDynamicState?.user?.FreeTrial;

  const freeTrialState: FreeTrialState = React.useMemo(
    () => ({
      company: {
        unconvertedFlowEnded:
          companyFreeTrial?.unconvertedFlowEnded ?? INITIAL_STATE_FREE_TRIAL_COMPANY.unconvertedFlowEnded,
        discountAvailableUntil: companyFreeTrial?.discountAvailableUntil
          ? new Date(companyFreeTrial.discountAvailableUntil)
          : INITIAL_STATE_FREE_TRIAL_COMPANY.discountAvailableUntil,
      },
      user: userFreeTrial ?? INITIAL_STATE_FREE_TRIAL_USER,
    }),
    [companyFreeTrial?.unconvertedFlowEnded, companyFreeTrial?.discountAvailableUntil, userFreeTrial]
  );

  const updateFreeTrialState = React.useCallback(
    (update: FreeTrialUpdateUser | FreeTrialUpdateCompany) => {
      if (update.scope === DynamicStateScopes.Company) {
        updateDynamicState({ scope: DynamicStateScopes.Company, state: { FreeTrial: { ...update.state } } });
      } else if (update.scope === DynamicStateScopes.User) {
        const updatedState: FreeTrialStateUser = {
          ...freeTrialState.user,
          ...update.state,
        };

        updateDynamicState({ scope: DynamicStateScopes.User, state: { FreeTrial: updatedState } });
      }
    },
    [freeTrialState.user, updateDynamicState]
  );

  return [freeTrialState, updateFreeTrialState];
}

export default useFreeTrialState;
